import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useFormikContext } from "formik";
import { useBreakpoints } from "@qgiv/core-react";
import {
    getHasDisplayableDedicationOnThisPage,
    prepareValuesForSubmit,
} from "@qgiv/donation-form";
import {
    selectCurrentPage,
    selectIsSinglePageForm,
    selectIsSubmittingUsingExpressCheckout,
} from "../redux/slices/appSettingsSlice";
import { selectAllFormSettings } from "../redux/slices/formSettingsSlice";
import { selectAllDonationDetails } from "../redux/slices/donationDetailsSlice";
import {
    selectAllFieldsAndGroups,
    selectAllDisplayableCustomFields,
    selectHasDisplayableMatchingGiftOnPage,
} from "../redux/slices/fieldsSlice";
import { selectDedicationSettings } from "../redux/slices/dedicationSettingsSlice";
import {
    selectCurrentDisplay,
    selectSubmissionType,
} from "../redux/slices/configSettingsSlice";
import { selectAllDonationSettings } from "../redux/slices/donationSettingsSlice";
import { selectAssocInfoUrlShortcuts } from "../redux/slices/urlShortcutsSlice";
import { selectRestrictions } from "../redux/slices/restrictionSettingsSlice";
import { selectSmsData } from "../redux/slices/smsDataSlice";
import { selectHasActiveFulfillment } from "../redux/slices/promiseTransactionSlice";
import {
    selectAbandonedGiftSource,
    selectAbandonedGiftToken,
} from "../redux/slices/abandonedGiftSlice";

/**
 * @param {object} props
 * @param {boolean} props.isValidating
 * @returns {object} prepared values
 */
const usePrepareValuesForSubmit = ({ isValidating = false }) => {
    const { isSmallScreen } = useBreakpoints();

    const isSinglePageForm = useSelector(selectIsSinglePageForm);

    const formSettings = useSelector(selectAllFormSettings);
    const currentPage = useSelector(selectCurrentPage);
    const donationDetailsFromSelector = useSelector(selectAllDonationDetails);
    const customFields = useSelector(selectAllDisplayableCustomFields);
    const donationSettings = useSelector(selectAllDonationSettings);
    const { activeAmounts = [], useDynamicAmounts = false } = donationSettings;
    const dedicationSettings = useSelector(selectDedicationSettings);
    const currentDisplay = useSelector(selectCurrentDisplay);
    const submissionType = useSelector(selectSubmissionType);
    const restrictions = useSelector(selectRestrictions);
    const hasDisplayableMatchingGiftOnThisPage = useSelector((state) =>
        selectHasDisplayableMatchingGiftOnPage(state, currentPage),
    );
    const assocInfo = useSelector(selectAssocInfoUrlShortcuts);

    const isSubmittingUsingExpressCheckout = useSelector(
        selectIsSubmittingUsingExpressCheckout,
    );
    const hasDisplayableDedicationOnThisPage =
        getHasDisplayableDedicationOnThisPage({
            currentDisplay,
            currentPage,
            dedicationSettings,
        });
    const hasActiveFulfillment = useSelector(selectHasActiveFulfillment);

    const { values } = useFormikContext();

    // To avoid collision, remap customFields in donationDetails to customFieldValues
    const donationDetails = {
        ...donationDetailsFromSelector,
        customFieldValues: { ...donationDetailsFromSelector.customFields },
    };

    const donationAmountFromRedux =
        donationDetailsFromSelector?.giftDetails?.subtotal;
    const hasSelectedRecurringDonation =
        donationDetailsFromSelector?.giftDetails?.hasSelectedRecurringDonation;

    const smsData = useSelector(selectSmsData);

    const allFieldsAndGroups = useSelector(selectAllFieldsAndGroups);

    const conditionalLogicOptions = useMemo(
        () => ({
            donationAmountFromRedux,
            hasSelectedRecurringDonation,
            usesReduxDonationAmountValue: true,
            usesReduxRecurringValue: true,
        }),
        [donationAmountFromRedux, hasSelectedRecurringDonation],
    );

    const abandonedGiftToken = useSelector(selectAbandonedGiftToken);
    const abandonedGiftSource = useSelector(selectAbandonedGiftSource);

    const preparedValues = prepareValuesForSubmit({
        abandonedGiftSource,
        abandonedGiftToken,
        activeAmounts,
        allFieldsAndGroups,
        assocInfo,
        conditionalLogicOptions,
        currentDisplay,
        currentPage,
        customFields,
        donationDetails,
        donationSettings,
        formSettings,
        hasActiveFulfillment,
        hasDisplayableDedicationOnThisPage,
        hasDisplayableMatchingGiftOnThisPage,
        isSinglePageForm,
        isSmallScreen,
        isSubmittingUsingExpressCheckout,
        isUsingSmartAmounts: useDynamicAmounts,
        isValidating,
        restrictions,
        smsData,
        submissionType,
        values,
    });

    return preparedValues;
};

export default usePrepareValuesForSubmit;
